import { supabase } from './supabaseClient';
import { uploadFileToSupabase } from './uploadFileToSupabase';
import { customAlphabet } from 'nanoid';
import { EmailService } from './sendEmailNotification';
import { convertWithCloudConvert } from './cloudConvert';

const generateReviewLink = () => {
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const nanoid = customAlphabet(alphabet, 8);
  return nanoid();
};

export interface CreateAnonymousReviewParams {
  // Step 1: File upload & basic info
  files: File[];
  title: string;
  description: string;
  creatorEmail: string;
  // Optional: preuploaded storage path (landing page direct upload)
  preuploadedStoragePath?: string;
  preuploadedFileName?: string;
  preuploadedFileType?: string;
  
  // Step 2: Review details & settings (matching AnonymousReviewData exactly)
  reviewerEmail: string;
  reviewerFirstName?: string;
  reviewerLastName?: string;
  deadlineEnabled?: boolean;
  deadline: string;
  deadlineTime?: string;
  reminderEnabled?: boolean;
  reminders: Array<{ type: string; customDate?: string; customTime?: string }>;
  customInstructions: string;
  timeZone: string;
  // Note: CloudConvert conversion works without access token in anonymous flow
}

export interface AnonymousReviewResult {
  tempReviewId: number;
  anonymousReviewId: string;
  reviewLink: string;
}

export class AnonymousReviewService {
  static async createAnonymousReview(params: CreateAnonymousReviewParams): Promise<AnonymousReviewResult> {
    try {
      const {
        files,
        title,
        description,
        creatorEmail,
        reviewerEmail,
        reviewerFirstName,
        reviewerLastName,
        deadlineEnabled,
        deadline,
        deadlineTime,
        reminderEnabled,
        reminders,
        customInstructions,
        timeZone,
        preuploadedStoragePath,
        preuploadedFileName,
        preuploadedFileType
      } = params;

      console.log('Creating anonymous review with params:', params);

      // 1. Upload and convert files to PDF (handle multiple files)
      let fileUrls: string[] = [];
      let primaryFileUrl = '';
      let primaryFileType = '';

      if (preuploadedStoragePath) {
        // Use preuploaded file from Storage (uploaded by landing page function)
        console.log('Using preuploaded storage path from landing page:', preuploadedStoragePath);
        const { data } = supabase.storage.from('anonymous-reviews').getPublicUrl(preuploadedStoragePath);
        const preuploadedUrl = data.publicUrl;
        primaryFileUrl = preuploadedUrl;
        primaryFileType = preuploadedFileType || 'application/octet-stream';
        fileUrls.push(preuploadedUrl);
      } else if (files && files.length > 0) {
        console.log(`Uploading ${files.length} files...`);
        for (const file of files) {
          try {
            // Upload original file first
            const originalFileUrl = await uploadFileToSupabase(file, 'anonymous-reviews');
            
            // Check if file needs conversion (not PDF or image)
            const isPdfOrImage = file.type.startsWith('image/') || file.type === 'application/pdf';
            let finalFileUrl = originalFileUrl;
            let finalFileType = file.type;
            
            if (!isPdfOrImage) {
              console.log('File is not PDF or image, converting with CloudConvert...');
              
              try {
                // Convert with CloudConvert (no access token needed for anonymous flow)
                const exportUrl = await convertWithCloudConvert({
                  fileUrl: originalFileUrl,
                  fileName: file.name,
                  convertTo: 'pdf',
                  // No accessToken needed - Supabase function uses service role
                });
                console.log('CloudConvert successful, export URL:', exportUrl);
                
                // Download the converted file
                const response = await fetch(exportUrl);
                if (!response.ok) {
                  throw new Error(`Failed to download converted file: ${response.status} ${response.statusText}`);
                }
                const blob = await response.blob();
                console.log('Downloaded converted file, size:', blob.size);
                
                // Create a File object for Supabase upload
                const convertedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.pdf'), { type: 'application/pdf' });
                
                // Upload converted file to Supabase Storage
                finalFileUrl = await uploadFileToSupabase(convertedFile, 'anonymous-reviews');
                finalFileType = 'application/pdf';
                console.log('Converted file upload successful:', finalFileUrl);
              } catch (conversionError) {
                console.warn('CloudConvert failed, using original file:', conversionError);
                // Fall back to original file if conversion fails
                finalFileUrl = originalFileUrl;
                finalFileType = file.type;
                
                // Log the specific error for debugging
                if (conversionError instanceof Error) {
                  console.error('CloudConvert error details:', conversionError.message);
                }
              }
            }
            
            fileUrls.push(finalFileUrl);
            if (!primaryFileUrl) {
              primaryFileUrl = finalFileUrl;
              primaryFileType = finalFileType;
            }
          } catch (uploadError) {
            console.error('Error uploading file:', file.name, uploadError);
            // Continue with other files even if one fails
          }
        }
        console.log('Files uploaded successfully:', fileUrls);
      }

      // 2. Generate review link
      const reviewLink = generateReviewLink();
      console.log('Generated review link:', reviewLink);

      // 3. Prepare deadline
      let finalDeadline = null;
      if (deadlineEnabled && deadline && deadlineTime) {
        finalDeadline = `${deadline}T${deadlineTime}`;
        console.log('Deadline set to:', finalDeadline);
      }

      // 4. Create review request in database
      const reviewRequestData = {
        type: 'document',
        file_url: primaryFileUrl || null,
        review_link: reviewLink,
        file_type: primaryFileType || null,
        deadline: finalDeadline,
        time_zone: timeZone,
        title,
        status: 'open',
        is_anonymous: true,
        temp_creator_email: creatorEmail,
        temp_reviewer_email: reviewerEmail,
        user_email: creatorEmail,
        custom_instructions: customInstructions || null,
        additional_files: fileUrls.length > 1 ? JSON.stringify(fileUrls.slice(1)) : null
      };

      console.log('Creating review request with data:', reviewRequestData);

      const { data: reviewRequest, error: reviewError } = await supabase
        .from('review_requests')
        .insert(reviewRequestData)
        .select()
        .single();

      if (reviewError) {
        console.error('Error creating anonymous review request:', reviewError);
        throw new Error(`Failed to create review request: ${reviewError.message}`);
      }

      console.log('Review request created:', reviewRequest);

      // 5. Create anonymous review record
      const anonymousReviewData = {
        temp_review_id: reviewRequest.id,
        creator_email: creatorEmail,
        reviewer_email: reviewerEmail
      };

      console.log('Creating anonymous review record with data:', anonymousReviewData);

      const { data: anonymousReview, error: anonymousError } = await supabase
        .from('anonymous_reviews')
        .insert(anonymousReviewData)
        .select()
        .single();

      if (anonymousError) {
        console.error('Error creating anonymous review record:', anonymousError);
        throw new Error(`Failed to create anonymous review record: ${anonymousError.message}`);
      }

      console.log('Anonymous review record created:', anonymousReview);

      // 6. Create reviewer record
      const reviewerData = {
        review_request_id: reviewRequest.id,
        reviewer_email: reviewerEmail,
        reviewer_first_name: reviewerFirstName || '',
        reviewer_last_name: reviewerLastName || '',
        status: 'pending',
        review_link: `${reviewLink}-reviewer`
      };

      console.log('Creating reviewer record with data:', reviewerData);

      const { error: reviewerError } = await supabase
        .from('review_reviewers')
        .insert(reviewerData);

      if (reviewerError) {
        console.error('Error creating reviewer record:', reviewerError);
        // Don't fail the whole process if reviewer creation fails
      } else {
        console.log('Reviewer record created successfully');
      }

      // 7. Create reminder records if enabled
      if (reminderEnabled && reminders && reminders.length > 0 && finalDeadline) {
        console.log('Creating reminder records...');
        await this.createReminderRecords(reviewRequest.id, reminders, finalDeadline, timeZone);
      }

      // 8. Send email notification to reviewer
      try {
        console.log('Sending email notification to reviewer:', reviewerEmail);
        
        const reviewUrl = `${window.location.origin}/review/${reviewLink}`;
        const reviewerName = [reviewerFirstName, reviewerLastName].filter(Boolean).join(' ') || 'Reviewer';
        
        await EmailService.sendReviewAssignedEmail(reviewerEmail, {
          reviewerName,
          reviewUrl,
          reviewTitle: title,
          creatorName: creatorEmail.split('@')[0], // Use email prefix as name
          creatorEmail: creatorEmail,
          deadline: finalDeadline ? new Date(finalDeadline).toLocaleDateString() : undefined,
          comment: customInstructions || undefined
        });
        
        console.log('Email notification sent successfully to reviewer');
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't fail the whole process if email fails
      }

      // 9. Send confirmation email to creator
      try {
        console.log('Sending confirmation email to creator:', creatorEmail);
        
        const reviewUrl = `${window.location.origin}/review/${reviewLink}`;
        const reviewerName = [reviewerFirstName, reviewerLastName].filter(Boolean).join(' ') || 'Reviewer';
        
        // Send a custom confirmation email to the creator
        await EmailService.sendReviewStatusUpdateEmail(creatorEmail, {
          statusTitle: 'Review Created Successfully',
          statusMessage: `Your review has been created and assigned to ${reviewerName} (${reviewerEmail}).`,
          reviewUrl,
          reviewTitle: title,
          reviewerName,
          deadline: finalDeadline ? new Date(finalDeadline).toLocaleDateString() : undefined,
          statusBadge: true,
          statusBadgeBg: '#10b981',
          statusBadgeBorder: '#059669',
          statusBadgeColor: '#ffffff',
          statusIcon: '✅',
          statusBadgeText: 'Created',
          statusDescription: `Review assigned to ${reviewerEmail}`
        });
        
        console.log('Confirmation email sent successfully to creator');
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the whole process if email fails
      }

      const result = {
        tempReviewId: reviewRequest.id,
        anonymousReviewId: anonymousReview.id,
        reviewLink
      };

      console.log('Anonymous review created successfully:', result);
      return result;

    } catch (error) {
      console.error('Error in createAnonymousReview:', error);
      
      // Provide user-friendly error messages like the main app
      if (error instanceof Error) {
        if (error.message.includes('JWT') || error.message.includes('Authentication expired')) {
          throw new Error('Your session has expired. Please refresh the page and try again.');
        }
        if (error.message.includes('CloudConvert failed')) {
          throw new Error('File conversion failed. Please try again or use a different file format.');
        }
        throw error;
      }
      
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private static async createReminderRecords(
    reviewRequestId: number, 
    reminders: Array<{ type: string; customDate?: string; customTime?: string }>,
    deadline: string,
    timeZone: string
  ) {
    try {
      const deadlineDate = new Date(deadline);
      const reminderRecords = [];

      for (const reminder of reminders) {
        let reminderDateTime: Date;

        if (reminder.type === 'custom' && reminder.customDate && reminder.customTime) {
          // Custom reminder time
          reminderDateTime = new Date(`${reminder.customDate}T${reminder.customTime}`);
        } else {
          // Preset reminder times relative to deadline
          reminderDateTime = new Date(deadlineDate);
          
          switch (reminder.type) {
            case '3-days':
              reminderDateTime.setDate(reminderDateTime.getDate() - 3);
              break;
            case '1-day':
              reminderDateTime.setDate(reminderDateTime.getDate() - 1);
              break;
            case '3-hours':
              reminderDateTime.setHours(reminderDateTime.getHours() - 3);
              break;
            default:
              console.warn('Unknown reminder type:', reminder.type);
              continue;
          }
        }

        // Only create reminder if it's in the future
        if (reminderDateTime > new Date()) {
                  reminderRecords.push({
          review_request_id: reviewRequestId,
          remind_at: reminderDateTime.toISOString(),
          reminder_type: reminder.type,
          status: 'pending',
          time_zone: timeZone
        });
        }
      }

      if (reminderRecords.length > 0) {
        console.log('Creating reminder records:', reminderRecords);
        
        const { error } = await supabase
          .from('review_reminders')
          .insert(reminderRecords.map(record => ({
            review_request_id: record.review_request_id,
            remind_at: record.remind_at,
            reminder_type: record.reminder_type,
            status: record.status,
            time_zone: record.time_zone
          })));

        if (error) {
          console.error('Error creating reminder records:', error);
          throw new Error(`Failed to create reminder records: ${error.message}`);
        }

        console.log(`Created ${reminderRecords.length} reminder records successfully`);
      } else {
        console.log('No valid future reminders to create');
      }
    } catch (error) {
      console.error('Error in createReminderRecords:', error);
      throw error;
    }
  }

  static async getAnonymousReview(anonymousReviewId: string) {
    try {
      const { data, error } = await supabase
        .from('anonymous_reviews')
        .select(`
          *,
          review_requests (
            id,
            title,
            review_link,
            status,
            created_at
          )
        `)
        .eq('id', anonymousReviewId)
        .single();

      if (error) {
        console.error('Error getting anonymous review:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getAnonymousReview:', error);
      return null;
    }
  }

  static async claimAnonymousReview(anonymousReviewId: string, clerkUserId: string) {
    try {
      // Update anonymous review to mark as claimed
      const { error: anonymousError } = await supabase
        .from('anonymous_reviews')
        .update({
          is_claimed: true,
          claimed_by_user_id: clerkUserId
        })
        .eq('id', anonymousReviewId);

      if (anonymousError) {
        console.error('Error claiming anonymous review:', anonymousError);
        throw new Error('Failed to claim anonymous review');
      }

      // Update review request to link to user
      const { error: reviewError } = await supabase
        .from('review_requests')
        .update({
          clerk_user_id: clerkUserId,
          claimed_by_user_id: clerkUserId,
          is_anonymous: false
        })
        .eq('id', (await this.getAnonymousReview(anonymousReviewId))?.temp_review_id);

      if (reviewError) {
        console.error('Error updating review request:', reviewError);
        throw new Error('Failed to update review request');
      }

      return true;
    } catch (error) {
      console.error('Error in claimAnonymousReview:', error);
      throw error;
    }
  }

  static async cleanupExpiredAnonymousReviews() {
    try {
      const { error } = await supabase.rpc('cleanup_expired_anonymous_reviews');
      if (error) {
        console.error('Error cleaning up expired anonymous reviews:', error);
      }
    } catch (error) {
      console.error('Error in cleanupExpiredAnonymousReviews:', error);
    }
  }
} 